<template>
  <div v-if="ticket" class="container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="ibox">
          <div class="ibox-title">
            Detalhes da multa
          </div>

          <div v-if="editable" class="ibox-content">
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="ticket-status">Status da multa</label>
                  <select id="ticket-status" class="form-control" v-model="editableStatus">
                    <option value="0">Sem informação</option>
                    <option value="100">Em aberto</option>
                    <option value="200">Vencido</option>
                    <option value="300">Prescrito</option>
                    <option value="500">Pago</option>
                    <option value="600">Cancelado</option>
                  </select>
                </div>

                <div class="form-group">
                  <label>
                    <input type="checkbox" v-model="removeAlerts" class="mr-2">
                    Remover alertas
                  </label>
                </div>

                <hr />

                <template v-for="(field, index) in customFields">
                  <div class="form-group" :key="field.name">
                    <div class="d-flex align-items-center justify-content-between">
                      <label :for="field.name">{{ field.name }}</label>
                      <div class="field-actions">
                        <label class="mr-3">
                          <span class="mr-2">Não alterar</span>
                          <input type="checkbox" ref="notChange" :checked="showAction(field.name, NOT_CHANGE)"
                            @click="toggleFieldAction(index, field.name, NOT_CHANGE)">
                        </label>
                        <label>
                          <span class="mr-2">Remover</span>
                          <input type="checkbox" ref="toRemove" :checked="showAction(field.name, TO_REMOVE)"
                            @click="toggleFieldAction(index, field.name, TO_REMOVE)">
                        </label>
                      </div>
                    </div>

                    <template v-if="showEditField(field.name)">
                      <div v-if="showAction(field.name, TO_REMOVE)" class="input-group">
                        <input class="form-control" disabled value="A remover" />
                      </div>

                      <div v-else-if="showAction(field.name, NOT_CHANGE)" class="input-group">
                        <input class="form-control" disabled :value="buildValueNotChange(field.name)" />
                      </div>

                      <template v-else>
                        <div v-if="field.kind !== 'select'" class="input-group">
                          <input :id="field.name" type="text" class="form-control"
                            v-model="customFieldsMap[field.name].value"
                            :placeholder="`Digite o valor para ${field.name}`">
                        </div>

                        <div v-else class="input-group">
                          <select :id="field.name" class="form-control" v-model="customFieldsMap[field.name].value">
                            <option v-for="option in field.values" :key="option" :value="option">
                              {{ option }}
                            </option>
                          </select>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
              </div>

              <div class="col-md-12">
                <hr />
                <button class="btn btn-outline mr-2" @click="save">Salvar</button>
                <button class="btn btn-outline" @click="editable = false">Cancelar</button>
              </div>
            </div>
          </div>

          <div v-else class="ibox-content">
            <div class="row">
              <div class="col-md-12">
                <p><strong>AIT:</strong> {{ ticket.ait }}</p>
                <p><strong>Placa:</strong> {{ ticket.placa }}</p>
                <p><strong>Renavam:</strong> {{ ticket.renavam }}</p>
                <p><strong>Número do Processo:</strong> {{ ticket.numero_processo }}</p>
                <p><strong>Status:</strong> {{ ticket.status | status }}</p>
                <p><strong>Situação:</strong> {{ ticket.situacao }}</p>
                <p><strong>Data da Infração:</strong> {{ ticket.ocorrido_em | datetime }}</p>
                <p><strong>Data de Vencimento:</strong> {{ ticket.vencimento_em | datetime }}</p>
                <p><strong>Valor:</strong> {{ ticket.valor_cents | price }}</p>
                <p><strong>Valor Pago:</strong> {{ ticket.valor_pago_cents | price }}</p>
                <p><strong>Órgão Autuador:</strong> {{ ticket.autuador | authority }}</p>
                <p><strong>Código da Multa:</strong> {{ ticket.codigo_multa }}</p>
                <p><strong>Descrição:</strong> {{ ticket.descricao }}</p>
                <p><strong>Criado em:</strong> {{ ticket.created_at | datetime }}</p>
                <p><strong>Modificado em:</strong> {{ ticket.updated_at | datetime }}</p>
                <p><strong>Última captura:</strong> {{ ticket.capturedAt || ticket.updated_at | datetime }}</p>
                <template if="ticket.messages.length > 0">
                  <p><strong>Mensagens:</strong> {{ formatMessages(ticket.messages) }}</p>
                </template>
              </div>
            </div>

            <template v-if="ticket.customFields">
              <hr />
              <div class="row">
                <div class="col-md-12">
                  <h4>Campos personalizados</h4>
                </div>
              </div>

              <div class="row">
                <div v-for="field in showCustomFields(ticket.customFields)" class=" col-md-12"
                  :key="`cs-${field.name}`">
                  <p>
                    <strong>{{ field.name }}:</strong> {{ field.value }}
                  </p>
                </div>
              </div>
            </template>

            <div class="row" v-if="$withPolicy('cm-embarcador', 'update')">
              <div class="col-md-12">
                <hr>
                <button class="btn btn-outline" @click="setEditable()">Editar</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ibox">
      <div class="ibox-title">
        Histórico
        <span class="float-right mr-3" style="cursor: pointer" @click="toggleRelation('events')">
          <EyeIcon v-if="mapRelation.events.show" />
          <EyeOffIcon v-else />
        </span>
      </div>
      <div class="ibox-content" v-show="mapRelation.events.show">
        <Events :ticket="ticket" v-if="mapRelation.events.loaded" />
      </div>
    </div>

    <div class="ibox">
      <div class="ibox-title">
        Arquivos disponíveis
        <span class="float-right mr-3" style="cursor: pointer" @click="toggleRelation('documents')">
          <EyeIcon v-if="mapRelation.documents.show" />
          <EyeOffIcon v-else />
        </span>
      </div>
      <div class="ibox-content" v-show="mapRelation.documents.show">
        <Documents :ticket="ticket" v-if="mapRelation.documents.loaded" />
      </div>
    </div>
  </div>

  <div v-else class="row">
    <div class="col-md-12">
      <div class="ibox">
        <div class="ibox-title">
          Carregando...
        </div>

        <div class="ibox-content">
          <div class="row">
            <div class="col-md-12">
              <h4 class="text-align-center">Carregando...</h4>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Documents from '@/components/cm-embarcador/Documents'
import EntityProxy from '@/models/EntityProxy'
import Events from '@/components/cm-embarcador/show/Events'
import GlobalLoading from '@/mixins/GlobalLoading'
import { IssuingAuthority } from '@/models/IssuingAuthority'

import { EyeIcon, EyeOffIcon } from 'vue-feather-icons'
import { mapMutations } from 'vuex'

export default {
  components: { Documents, Events, EyeIcon, EyeOffIcon },
  created() {
    this.setNameBreadcrumb('...')
    this.load()
  },
  data: () => ({
    ticket: null,
    editable: false,
    editableStatus: '0',
    removeAlerts: false,
    customFields: [],
    loadedCustomFields: false,
    customFieldsMap: {},
    TO_REMOVE: 'toRemove',
    TO_UPDATE: 'toUpdate',
    NOT_CHANGE: 'notChange',
    mapRelation: {
      events: { loaded: false, show: false },
      documents: { loaded: false, show: false }
    }
  }),
  methods: {
    ...mapMutations(['setNameBreadcrumb']),
    buildValueNotChange(fieldName) {
      return this.ticket.customFields[fieldName] ? `Não alterar: "${this.ticket.customFields[fieldName]}"` : ''
    },
    load() {
      const ticket = this.$ls.get('show_cm-embarcador/multa')
      if (ticket) {
        this.ticket = EntityProxy.build(ticket)
        this.setNameBreadcrumb(this.ticket.ait)
        return
      }

      this.axios.v3.get(`/cm-embarcador/multas/${this.id}?methods=custom_fields`)
        .then(response => this.ticket = EntityProxy.build(response.data))
        .then(() => this.setNameBreadcrumb(this.ticket.ait))
        .catch(e => this.$notify.addError(e))
    },
    loadCustomFields() {
      return this.axios.v3.get('/custom_fields?q[resource]=cm-embarcador/multa&meta=false&per_page=100000')
        .then(response => { this.customFields = response.data })
        .then(() => this.loadedCustomFields = true)
    },
    toggleRelation(name) {
      this.mapRelation[name]['show'] = !this.mapRelation[name]['show']
      if (this.mapRelation[name]['show'])
        this.mapRelation[name]['loaded'] = true
    },
    reloadRelation(name) {
      this.mapRelation[name]['loaded'] = false
      this.mapRelation[name]['show'] = false
    },
    initializeCustomFieldsMap() {
      this.customFields.forEach(field => {
        const currentValue = this.ticket.customFields ? this.ticket.customFields[field.name] : ''
        this.$set(this.customFieldsMap, field.name, {
          value: currentValue || '',
          action: this.NOT_CHANGE
        })
      })
    },
    setEditable() {
      if (!this.loadedCustomFields)
        this.loadCustomFields().then(() => this.initializeCustomFieldsMap())
      else
        this.initializeCustomFieldsMap()

      this.editableStatus = this.ticket.status || '0'
      this.removeAlerts = false
      this.editable = true
    },
    toggleFieldAction(index, fieldName, action) {
      if (this.customFieldsMap[fieldName].action === action) {
        this.customFieldsMap[fieldName].action = this.TO_UPDATE
      } else {
        this.customFieldsMap[fieldName].action = action
        const targetAction = action === this.TO_REMOVE ? 'notChange' : 'toRemove'
        this.uncheckElement(targetAction, index)
      }
    },
    uncheckElement(targetAction, index) {
      this.$refs[targetAction][index].checked = false
    },
    showAction(fieldName, action) {
      return this.customFieldsMap[fieldName] && this.customFieldsMap[fieldName].action === action
    },
    showEditField(fieldName) {
      return this.customFieldsMap[fieldName]
    },
    save() {
      const customFields = {}
      const currentCustomFields = EntityProxy.clone(this.ticket.customFields)

      Object.entries(this.customFieldsMap).forEach(([key, map]) => {
        if (map.action === this.TO_REMOVE) {
          customFields[key] = null
          this.$delete(currentCustomFields, key)
        } else if (map.action === this.TO_UPDATE) {
          customFields[key] = map.value
        }
      })

      const params = {
        status: this.editableStatus,
        custom_fields: customFields
      }

      if (this.removeAlerts)
        params.remove_alerts = true

      this.openGlobalLoad()
      this.axios.v3.put(`/cm-embarcador/multas/${this.ticket.id}`, params)
        .then(response => this.ticket = EntityProxy.build(response.data))
        .then(() => {
          this.ticket.status = this.editableStatus
          this.ticket.customFields = currentCustomFields
          Object.entries(customFields).forEach(([key, value]) => {
            if (value === null)
              this.$delete(this.ticket.customFields, key)
            else
              this.$set(this.ticket.customFields, key, value)
          })
        })
        .then(() => this.$notify.add('Multa atualizada com sucesso!'))
        .then(() => this.editable = false)
        .then(() => this.reloadRelation('events'))
        .catch(e => this.$notify.addError(e))
        .finally(() => this.closeGlobalLoad())
    },
    showCustomFields(customFields) {
      return Object.entries(customFields).map(([key, value]) => {
        return { name: key, value }
      })
    },
    formatMessages(messages) {
      if (!messages) return ''

      return messages.replace(/warning:/g, 'AVISO: ')
        .replace(/error:/g, 'ERRO: ')
        .replace(/info:/g, 'INFORMAÇÃO: ')
        .replace(/;/g, '\n')
    }

  },
  mixins: [GlobalLoading],
  name: 'Show',
  filters: {
    authority(value) {
      return IssuingAuthority.label(value)
    },
    status(value) {
      return {
        '0': 'Sem status',
        '100': 'Em aberto',
        '200': 'Vencido',
        '300': 'Prescrito',
        '400': 'Sem informação',
        '500': 'Pago',
        '600': 'Cancelado',
      }[value]
    },
  },
  props: {
    id: { required: false }
  },
  watch: {
    ticket(newTicket) {
      if (!newTicket.customFields || typeof newTicket.customFields !== 'object')
        this.$set(this.ticket, 'customFields', {})
    }
  }
}
</script>

<style scoped>
.field-actions {
  margin-left: 10px;
}

.input-group {
  margin-bottom: 0;
}
</style>
